import React, { useState, useMemo, useEffect } from 'react';
import { DeepDiveCardProvider } from '@atlassian/jira-deep-dive-cards-section/src/services/deep-dive-provider/index.tsx';
import { DeepDiveCardsSection } from '@atlassian/jira-deep-dive-cards-section/src/ui/index.tsx';
import { RecentsContainer } from '@atlassian/jira-recents-store';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { InitialDataLoader } from './initial-data-loader.tsx';
import type { Props } from './types.tsx';
import { getRecentProjects, getRecent2QueuesByRecentProjects } from './utils.tsx';

const StableRecentProjects = ({ prefetchResult }: Props): React.JSX.Element => {
	const [stablePrefectResult, setStablePrefectResult] =
		useState<Props['prefetchResult']>(prefetchResult);

	const { recentProjects, recent2QueuesByRecentProjects } = useMemo(() => {
		const recent = getRecentProjects(stablePrefectResult);
		const recent2Queues = getRecent2QueuesByRecentProjects(stablePrefectResult, recent);
		return {
			recentProjects: recent,
			recent2QueuesByRecentProjects: recent2Queues,
		};
	}, [stablePrefectResult]);

	const cloudId = useCloudId();
	const applicationPermissions = useApplicationPermissions();

	// eslint-disable-next-line react-you-might-not-need-an-effect/no-reset-all-state-on-prop-change
	useEffect(() => {
		if (
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
			!stablePrefectResult ||
			!stablePrefectResult.data ||
			!stablePrefectResult.data.tabs ||
			!stablePrefectResult.data.tabs.RecentlyViewed ||
			!stablePrefectResult.data.tabs.RecentlyViewed.data ||
			!stablePrefectResult.data.tabs.RecentlyViewed.data.sections ||
			stablePrefectResult.data.tabs.RecentlyViewed.data.sections.length === 0
		) {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
			setStablePrefectResult(prefetchResult);
		}
	}, [prefetchResult, stablePrefectResult]);

	return (
		<>
			<RecentsContainer
				isGlobal
				cloudId={cloudId}
				applicationPermissions={applicationPermissions}
			/>

			{!__SERVER__ && <InitialDataLoader />}
			<DeepDiveCardProvider
				recentProjects={recentProjects}
				recent2QueuesByRecentProjects={recent2QueuesByRecentProjects}
				isLoading={stablePrefectResult ? stablePrefectResult.loading : true}
				recommendedProjectsData={
					expValEquals('recommended_projects_for_you_page', 'cohort', 'test', 'control')
						? prefetchResult?.data?.recommendedProjectsData
						: undefined
				}
			>
				<DeepDiveCardsSection />
			</DeepDiveCardProvider>
		</>
	);
};

export default StableRecentProjects;
